import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Play, ExternalLink, Music, Crown, ArrowRight, Headphones, Tv } from "lucide-react";

export default function StreamingBasic() {
  const { user } = useAuth();
  const isFull = user && !["free", "basic"].includes(user.subscription_tier);

  if (isFull) {
    return <Navigate to="/streaming" replace />;
  }

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="streaming-basic-page">
        {/* Header */}
        <div>
          <p className="overline-text mb-2">SMAHo.OS Streaming Services</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-light text-spa-moss mb-3">
            Rice And Peas For The Soul TV
          </h1>
          <p className="text-spa-text-secondary max-w-2xl">
            Watch wellness content from Mr. RAPsoul.R. Upgrade to Premium for full access to our streaming library, on-demand sessions, and curated playlists.
          </p>
        </div>

        {/* Featured Video */}
        <div className="spa-card" data-testid="basic-featured-video">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-sm">
              <Play className="w-5 h-5 text-white fill-white" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-serif text-xl text-spa-moss">Rice And Peas For The Soul Online TV</h3>
              <p className="text-xs text-spa-text-secondary">Featured wellness streaming from Mr. RAPsoul.R</p>
            </div>
          </div>
          <div className="relative w-full rounded-2xl overflow-hidden" style={{ paddingBottom: "56.25%" }}>
            <iframe
              className="absolute inset-0 w-full h-full rounded-2xl"
              src="https://www.youtube.com/embed/iUuVXNIYhjE?si=PvK03b8OdwHN7doM"
              title="Rice And Peas For The Soul Online TV"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </div>

        {/* Channel Links */}
        <div className="grid sm:grid-cols-2 gap-4" data-testid="channel-links">
          <a
            href="https://www.youtube.com/@rapSOULrTV"
            target="_blank"
            rel="noopener noreferrer"
            className="spa-card flex items-center gap-4 hover:-translate-y-1 transition-transform duration-200"
            data-testid="youtube-channel-link"
          >
            <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center flex-shrink-0">
              <Tv className="w-7 h-7 text-white" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-serif text-lg text-spa-moss">RAPsoul.R Online TV</h3>
              <p className="text-xs text-spa-text-secondary">YouTube Main Channel</p>
            </div>
            <ExternalLink className="w-5 h-5 text-spa-text-secondary flex-shrink-0" />
          </a>

          <a
            href="https://open.spotify.com/show/therecoveryshowsmahoody"
            target="_blank"
            rel="noopener noreferrer"
            className="spa-card flex items-center gap-4 hover:-translate-y-1 transition-transform duration-200"
            data-testid="spotify-channel-link"
          >
            <div className="w-14 h-14 bg-gradient-to-br from-[#1DB954] to-[#158a3e] rounded-2xl flex items-center justify-center flex-shrink-0">
              <Headphones className="w-7 h-7 text-white" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-serif text-lg text-spa-moss">The Recovery Show SMAHoody</h3>
              <p className="text-xs text-spa-text-secondary">Spotify Podcast Channel</p>
            </div>
            <ExternalLink className="w-5 h-5 text-spa-text-secondary flex-shrink-0" />
          </a>
        </div>

        {/* Upgrade CTA */}
        <div className="relative overflow-hidden rounded-3xl border border-amber-200/50 bg-gradient-to-r from-amber-50 via-white to-amber-50/50 p-8" data-testid="streaming-upgrade-cta">
          <div className="absolute -right-10 -top-10 w-40 h-40 bg-amber-100/50 rounded-full blur-3xl" />
          <div className="relative flex flex-col md:flex-row items-center gap-6">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-amber-50 rounded-2xl flex items-center justify-center flex-shrink-0">
              <Crown className="w-8 h-8 text-amber-500" strokeWidth={1.5} />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="font-serif text-2xl text-spa-moss mb-2">Unlock Full Streaming Library</h3>
              <p className="text-spa-text-secondary text-sm max-w-lg">
                Get access to the complete streaming library — on-demand wellness videos, curated Spotify playlists, SMAHooBITs guides, Laughing Therapist sessions, and more.
              </p>
            </div>
            <Link to="/pricing" className="spa-button-primary px-6 py-3 gap-2 flex-shrink-0" data-testid="streaming-upgrade-btn">
              <Crown className="w-4 h-4" /> Upgrade Now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
